import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { CharJudgment, MarkType } from "@xinghai/shared";
import { toToneMarks } from "@xinghai/shared";
import { Button, Field, Select, Sheet, useToast } from "@xinghai/ui";
import { ApiError, goldenApi } from "../../../lib/api";
import { FINAL_OPTIONS, INITIAL_OPTIONS, MARK_LABEL, MARK_ORDER } from "../labels";
import { DEFAULT_SCORE } from "../draft";
import type { AnnotateMaterialChar } from "../types";

export interface GoldenSheetProps {
  open: boolean;
  onClose: () => void;
  recordingId: string;
  /** The material rows this recording was produced from. */
  chars: readonly AnnotateMaterialChar[];
  /** `singing` never carries a tone (红线 14.1). @default "reading" */
  domain?: "reading" | "singing";
  /** Prefill when the recording is already a golden item. */
  initialAnswer?: CharJudgment[] | null;
  onSaved?: (golden: boolean) => void;
}

interface Row {
  mark_type: MarkType;
  realized: string | null;
}

function blankRows(count: number, answer?: CharJudgment[] | null): Row[] {
  return Array.from({ length: count }, (_, i) => ({
    mark_type: answer?.[i]?.mark_type ?? "correct",
    realized: answer?.[i]?.realized ?? null,
  }));
}

/**
 * docs/ROADMAP.md A5 — 黄金题录入 (§1.5 8% 隐藏黄金题, 命中率 ≥90%).
 *
 * Course-team only. Deliberately a standalone component with no route of its
 * own: the 质检 / 说话人 screens (admin feature) mount it where they need it.
 * `POST recordings/:id/golden` with `answer: null` clears the flag.
 */
export function GoldenSheet({
  open,
  onClose,
  recordingId,
  chars,
  domain = "reading",
  initialAnswer = null,
  onSaved,
}: GoldenSheetProps) {
  const toast = useToast();
  const [rows, setRows] = useState<Row[]>(() => blankRows(chars.length, initialAnswer));

  useEffect(() => {
    if (open) setRows(blankRows(chars.length, initialAnswer));
  }, [open, chars.length, initialAnswer]);

  const save = useMutation({
    mutationFn: (answer: CharJudgment[] | null) => goldenApi.set(recordingId, answer),
    onSuccess: (res) => {
      toast.success(res.golden ? "已设为黄金题。" : "已取消黄金题。");
      onSaved?.(res.golden);
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "保存失败，请重试。"),
  });

  const toAnswer = (): CharJudgment[] =>
    rows.map((r, char_index) => {
      const correct = r.mark_type === "correct";
      const score = correct ? DEFAULT_SCORE : 0;
      const base = {
        char_index,
        mark_type: r.mark_type,
        realized: r.realized,
        note: null,
      };
      if (domain === "singing") {
        // 红线 14.1 — no tone anywhere in the singing domain.
        return {
          ...base,
          initial: null,
          final: null,
          tone: null,
          realized_tone: null,
          onset: score,
          nucleus: score,
          coda: score,
        } satisfies CharJudgment;
      }
      return {
        ...base,
        initial: score,
        final: score,
        tone: score,
        realized_tone: null,
      } satisfies CharJudgment;
    });

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="设为黄金题"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => save.mutate(null)} loading={save.isPending}>
            取消黄金题
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            关闭
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => save.mutate(toAnswer())}
          >
            保存标准答案
          </Button>
        </>
      }
    >
      <p className="gz-caption">
        黄金题会按 8%
        混入专家队列且不做标记，用于计算命中率。标准答案只记录每个字的标记类型和实现音素。
      </p>
      <ul className="xz-golden__rows">
        {chars.map((ch, i) => (
          <li key={`${ch.char}-${i}`} className="xz-golden__row">
            <span className="xz-arb__glyph">
              <span className="xz-arb__char">{ch.char}</span>
              <span className="xz-arb__pinyin">{toToneMarks(ch.pinyin_locked)}</span>
            </span>
            <Field label={`第 ${i + 1} 字标记类型`} hideLabel>
              <Select
                aria-label={`第 ${i + 1} 字标准答案标记类型`}
                value={rows[i]?.mark_type ?? "correct"}
                onChange={(e) => setRow(i, { mark_type: e.target.value as MarkType })}
              >
                {MARK_ORDER.map((m) => (
                  <option key={m} value={m}>
                    {MARK_LABEL[m]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={`第 ${i + 1} 字实现音素`} hideLabel>
              <Select
                aria-label={`第 ${i + 1} 字标准答案实现音素`}
                value={rows[i]?.realized ?? ""}
                disabled={rows[i]?.mark_type === "correct"}
                onChange={(e) => setRow(i, { realized: e.target.value || null })}
              >
                <option value="">未选择</option>
                <optgroup label="声母">
                  {INITIAL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="韵母">
                  {FINAL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </Field>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}
